/*
 * Embroidery personalization fields - Dawn 15.5.0 extension.
 *
 * Drei Modi:
 *  standard/upload -> Radio "Wie auf dem Titelbild"/"Personalisieren" steuert
 *                      Panel auf/zu (bestehendes Verhalten, unveraendert).
 *  position        -> keine Wahl, immer aktiv. Deckt sowohl variantengetriebene
 *                      Vorne/Hinten/Seitlich-Felder (position/wunschtext-1) als
 *                      auch feste Zeilenanzahl ohne Variante (wunschtext-2/3) ab.
 *                      Die native Textposition-Variante bekommt zusaetzlich die
 *                      Klasse lc-position-picker fuers Etsy-Orange-Design, falls
 *                      vorhanden - ohne Variante passiert dort einfach nichts.
 *
 * DATEI-UPLOAD: Shopifys AJAX-Cart-API akzeptiert keine Dateien. Sobald eine
 * Datei gewaehlt ist, wird Dawns AJAX-Handler abgeschaltet (stopImmediatePropagation
 * OHNE preventDefault) und der Browser sendet das Formular klassisch an /cart/add.
 */

if (!customElements.get('personalization-fields')) {
  const ALLOWED_FILE_TYPES = ['png', 'jpg', 'jpeg', 'pdf'];
  const MAX_FILE_BYTES = 20 * 1024 * 1024; // Shopify-Limit: 20 MB

  class PersonalizationFields extends HTMLElement {
    constructor() {
      super();
      this.onSubmitCapture = this.onSubmitCapture.bind(this);
      this.validateOnBlur = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.positionMode = this.hasAttribute('data-position-mode');

      this.form = this.closest('form');
      this.panel = this.querySelector('[data-personalization-panel]');
      this.modeInputs = Array.from(this.querySelectorAll('[data-personalization-mode]'));
      this.inputs = Array.from(this.querySelectorAll('[data-personalization-input]'));
      this.selects = Array.from(this.querySelectorAll('[data-personalization-select]'));
      this.fileInput = this.querySelector('[data-personalization-file]');
      this.fileNameWrapper = this.querySelector('[data-personalization-filename]');
      this.fileNameText = this.querySelector('[data-personalization-filename-text]');
      this.fileRemoveButton = this.querySelector('[data-personalization-file-remove]');
      this.errorWrapper = this.querySelector('[data-personalization-error]');
      this.errorText = this.querySelector('[data-personalization-error-text]');
      this.summaryEl = this.querySelector('[data-personalization-summary]');
      this.summaryBody = this.querySelector('[data-personalization-summary-body]');

      this.fields = this.inputs.concat(this.selects);
      if (this.fileInput) this.fields.push(this.fileInput);

      this.modeInputs.forEach((radio) => {
        radio.addEventListener('change', () => this.applyMode());
      });

      this.inputs.forEach((input) => {
        this.updateCounter(input);

        input.addEventListener('input', () => {
          this.updateCounter(input);
          this.updateSummary();
          if (input.getAttribute('aria-invalid') === 'true' && input.value.trim() !== '') {
            this.clearError();
          }
        });

        input.addEventListener('blur', () => {
          input.value = input.value.replace(/\s+/g, ' ').trim();
          this.updateCounter(input);
          this.updateSummary();
          if (this.validateOnBlur) this.validate();
        });
      });

      this.selects.forEach((select) => {
        select.addEventListener('change', () => {
          this.updateSummary();
          if (select.getAttribute('aria-invalid') === 'true' && select.value !== '') {
            this.clearError();
          }
        });
      });

      if (this.fileInput) {
        this.fileInput.addEventListener('change', () => {
          this.updateFileName();
          const problem = this.checkFile();
          if (problem) {
            this.fileInput.setAttribute('aria-invalid', 'true');
            this.showError(problem);
          } else {
            this.clearError();
          }
        });
      }

      if (this.fileRemoveButton) {
        this.fileRemoveButton.addEventListener('click', () => {
          if (!this.fileInput) return;
          this.fileInput.value = '';
          this.updateFileName();
          this.clearError();
        });
      }

      if (this.positionMode) {
        this.initPositionMode();
      } else {
        this.applyMode();
      }

      if (!this.form) return;

      // Ancestor + capture phase guarantees this runs before Dawn's handler.
      this.submitScope = this.closest('product-form') || this.form.parentElement || this.form;
      this.submitScope.addEventListener('submit', this.onSubmitCapture, true);
    }

    disconnectedCallback() {
      if (this.submitScope) {
        this.submitScope.removeEventListener('submit', this.onSubmitCapture, true);
      }
      if (this.pickerObserver) this.pickerObserver.disconnect();
    }

    /* ---------- Standard / Upload: Radio "Wie auf dem Titelbild" ---------- */

    get isCustom() {
      if (this.positionMode) return true;
      const selected = this.modeInputs.find((radio) => radio.checked);
      return Boolean(selected) && selected.dataset.personalizationMode === 'custom';
    }

    applyMode() {
      const custom = this.isCustom;

      if (this.panel) this.panel.hidden = !custom;
      this.fields.forEach((field) => {
        field.disabled = !custom;
      });

      if (!custom) {
        this.validateOnBlur = false;
        if (this.fileInput) this.fileInput.value = '';
        this.updateFileName();
        this.clearError();
      }
    }

    /* ---------- Position: Textfelder folgen (falls vorhanden) der Varianten-Auswahl ---------- */

    initPositionMode() {
      this.stylePositionPicker();
      const initial = this.currentPositionValue();
      this.applyPositionTier(this.tierFor(initial));
      document.addEventListener('change', (event) => this.onFormChange(event));

      // Dawn tauscht die Textposition-Buttons bei jeder Auswahl per Ajax neu ein -
      // die Orange-Markierung geht dabei verloren. Bei jeder DOM-Aenderung neu markieren.
      this.pickerObserver = new MutationObserver(() => this.stylePositionPicker());
      this.pickerObserver.observe(document.body, { childList: true, subtree: true });
    }

    stylePositionPicker() {
      document.querySelectorAll('.lc-position-picker').forEach((el) => {
        el.classList.remove('lc-position-picker');
      });

      const marker = document.querySelector(
        'input[type="radio"][value*="orne" i], select option[value*="orne" i]'
      );
      if (!marker) return;

      const scope =
        marker.closest('fieldset') ||
        marker.closest('.product-form__input') ||
        marker.closest('variant-radios, variant-selects') ||
        marker.parentElement;

      if (scope) scope.classList.add('lc-position-picker');
    }

    currentPositionValue() {
      const checked = document.querySelector('input[type="radio"][value*="orne" i]:checked');
      if (checked) return checked.value;

      const selects = document.querySelectorAll('select');
      for (const select of selects) {
        if (select.value && select.value.toLowerCase().includes('vorne')) return select.value;
      }
      return '';
    }

    tierFor(value) {
      const v = (value || '').toLowerCase();
      if (v.includes('seitlich')) return 3;
      if (v.includes('hinten')) return 2;
      return 1;
    }

    onFormChange(event) {
      const el = event.target;
      const isOptionInput =
        el && ((el.tagName === 'INPUT' && el.type === 'radio') || el.tagName === 'SELECT');
      if (!isOptionInput) return;

      const value = (el.value || '').toLowerCase();
      if (!value.includes('vorne')) return; // andere Option (z. B. Farbe) geaendert, ignorieren

      this.applyPositionTier(this.tierFor(el.value));
    }

    applyPositionTier(tier) {
      this.positionTier = tier;

      ['back', 'side'].forEach((key) => {
        const wrap = this.querySelector(`[data-position-field="${key}"]`);
        if (!wrap) return;

        const input = wrap.querySelector('input');
        const needed = key === 'back' ? tier >= 2 : tier >= 3;

        wrap.hidden = !needed;
        if (input) {
          input.disabled = !needed;
          if (!needed) {
            input.value = '';
            input.setAttribute('aria-invalid', 'false');
            this.updateCounter(input);
          }
        }
      });

      this.updateSummary();
    }

    /* ---------- Gemeinsame Logik ---------- */

    onSubmitCapture(event) {
      if (event.target !== this.form) return;
      if (!this.isCustom) return;

      this.inputs.forEach((input) => {
        input.value = input.value.replace(/\s+/g, ' ').trim();
        this.updateCounter(input);
      });

      if (!this.validate()) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const firstInvalid = this.fields.find((field) => field.getAttribute('aria-invalid') === 'true');
        if (firstInvalid) {
          const focusTarget =
            firstInvalid === this.fileInput
              ? this.querySelector('.personalization__dropzone')
              : firstInvalid;
          if (focusTarget && focusTarget.focus) focusTarget.focus();
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      // Datei dabei -> Dawns AJAX abschalten, Browser sendet klassisch (Redirect auf /cart).
      if (this.hasFile) {
        event.stopImmediatePropagation();
      }
    }

    get hasFile() {
      return Boolean(
        this.fileInput && !this.fileInput.disabled && this.fileInput.files && this.fileInput.files.length
      );
    }

    validate() {
      if (!this.isCustom) {
        this.clearError();
        return true;
      }

      this.validateOnBlur = true;
      let message = '';

      this.fields.forEach((field) => {
        if (field.disabled) {
          field.setAttribute('aria-invalid', 'false');
          return;
        }

        const required = field.hasAttribute('data-personalization-required');
        const label = this.labelFor(field);
        let fieldMessage = '';

        if (field === this.fileInput) {
          if (required && !this.hasFile) {
            fieldMessage = 'Bitte lade deine Datei hoch.';
          } else {
            fieldMessage = this.checkFile() || '';
          }
        } else {
          const value = field.value.trim();

          if (required && value === '') {
            fieldMessage =
              field.tagName === 'SELECT'
                ? `Bitte wähle eine ${label}.`
                : `Bitte gib die ${label} ein.`;
          } else if (field.maxLength > 0 && value.length > field.maxLength) {
            fieldMessage = `${label} darf maximal ${field.maxLength} Zeichen haben.`;
          }
        }

        field.setAttribute('aria-invalid', fieldMessage ? 'true' : 'false');
        if (fieldMessage && !message) message = fieldMessage;
      });

      if (message) {
        this.showError(message);
        return false;
      }

      this.clearError();
      return true;
    }

    checkFile() {
      if (!this.hasFile) return '';

      const file = this.fileInput.files[0];
      const extension = (file.name.split('.').pop() || '').toLowerCase();

      if (ALLOWED_FILE_TYPES.indexOf(extension) === -1) {
        return 'Erlaubt sind nur PNG, JPG und PDF.';
      }

      if (file.size > MAX_FILE_BYTES) {
        return 'Die Datei ist zu groß. Maximal 20 MB sind möglich.';
      }

      return '';
    }

    updateFileName() {
      if (!this.fileNameWrapper) return;

      if (this.hasFile) {
        const file = this.fileInput.files[0];
        const megabytes = (file.size / (1024 * 1024)).toFixed(1).replace('.', ',');
        if (this.fileNameText) this.fileNameText.textContent = `${file.name} (${megabytes} MB)`;
        this.fileNameWrapper.removeAttribute('hidden');
      } else {
        if (this.fileNameText) this.fileNameText.textContent = '';
        this.fileNameWrapper.setAttribute('hidden', '');
      }
    }

    showError(message) {
      if (!this.errorWrapper) return;
      if (this.errorText) this.errorText.textContent = message;
      this.errorWrapper.removeAttribute('hidden');
    }

    clearError() {
      this.fields.forEach((field) => field.setAttribute('aria-invalid', 'false'));
      if (!this.errorWrapper) return;
      this.errorWrapper.setAttribute('hidden', '');
      if (this.errorText) this.errorText.textContent = '';
    }

    updateCounter(input) {
      const field = input.closest('.personalization__field');
      const counter = field && field.querySelector('[data-personalization-counter]');
      if (!counter) return;

      const max = input.maxLength > 0 ? input.maxLength : 20;
      const length = input.value.length;

      counter.textContent = `${length} / ${max}`;
      counter.classList.toggle('is-max', length >= max);
    }

    /* Zeigt jedes ausgefuellte, aktive Feld generisch - unabhaengig davon, ob es
       Vorne/Hinten/Seitlich, Erste/Zweite/Dritte Zeile oder etwas ganz anderes ist. */
    updateSummary() {
      if (!this.summaryEl || !this.summaryBody) return;

      this.summaryBody.textContent = '';
      let any = false;

      this.fields.forEach((field) => {
        if (field === this.fileInput || field.disabled) return;

        const value = field.value ? field.value.trim() : '';
        if (!value) return;

        const label = this.labelFor(field);
        const line = document.createElement('span');
        line.textContent = `${label}: ${value}`;
        this.summaryBody.appendChild(line);
        any = true;
      });

      this.summaryEl.hidden = !any;
    }

    labelFor(field) {
      if (field.dataset.personalizationLabel) return field.dataset.personalizationLabel;
      const label = this.querySelector(`label[for="${field.id}"]`);
      if (!label) return 'dieses Feld';
      return label.textContent.replace('*', '').trim();
    }
  }

  customElements.define('personalization-fields', PersonalizationFields);
}